import React, { useState } from 'react';
import {
  X,
  ShoppingBag,
  Trash2,
  Tag,
  ArrowRight,
  ShieldCheck,
  CheckCircle2,
  Sparkles
} from 'lucide-react';
import { CartItem, Coupon, Currency } from '../types';
import { getSafeProductFirstImage } from '../lib/productImages';

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  cartItems: CartItem[];
  onUpdateQuantity: (productId: string, qty: number) => void;
  onRemoveItem: (productId: string) => void;
  appliedCoupon: Coupon | null;
  onApplyCoupon: (code: string) => Promise<boolean>;
  onProceedToCheckout: () => void;
  onNavigateProduct?: (product: any) => void;
  currency: Currency;
  darkMode: boolean;
}

export const CartDrawer: React.FC<CartDrawerProps> = ({
  isOpen,
  onClose,
  cartItems,
  onUpdateQuantity,
  onRemoveItem,
  appliedCoupon,
  onApplyCoupon,
  onProceedToCheckout,
  onNavigateProduct,
  currency,
  darkMode,
}) => {
  const [couponInput, setCouponInput] = useState('');
  const [couponError, setCouponError] = useState('');
  const [couponSuccess, setCouponSuccess] = useState('');

  if (!isOpen) return null;

  const currencySymbols: Record<Currency, string> = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    };

  const exchangeRates: Record<Currency, number> = {
    USD: 1,
    EUR: 0.92,
    GBP: 0.78,
    };

  const symbol = currencySymbols[currency];
  const rate = exchangeRates[currency];

  const subtotal = cartItems.reduce((acc, item) => acc + item.product.price * item.quantity, 0);
  const discountAmount = appliedCoupon ? (subtotal * appliedCoupon.discountPercent) / 100 : 0;
  const grandTotal = Math.max(0, subtotal - discountAmount);

  const handleApply = async () => {
    setCouponError('');
    setCouponSuccess('');
    if (!couponInput.trim()) return;

    const ok = await onApplyCoupon(couponInput);
    if (ok) {
      setCouponSuccess('Coupon applied successfully!');
      setCouponInput('');
    } else {
      setCouponError('Invalid coupon. Try RH3D20 or WELCOME10');
    }
  };

  return (
    <div className="fixed inset-0 z-[999] overflow-hidden bg-slate-950/95  flex items-center justify-center sm:items-stretch sm:justify-end">
      
      {/* Glowing Border Wrapper */}
      <div className="relative w-[92vw] h-[92vh] sm:w-[450px] md:w-[480px] sm:h-auto sm:my-4 sm:mr-4 rounded-[24px] flex flex-col max-h-[92vh] sm:max-h-[calc(100vh-32px)] p-[2px] bg-gradient-to-br from-[#0070f3] via-[#8b5cf6] to-[#06b6d4] shadow-[0_0_30px_rgba(139,92,246,0.2)] transition-transform duration-300 shrink-0">
        
        <div className={`relative w-full h-full flex flex-col justify-between overflow-hidden rounded-[22px] ${
          darkMode ? 'bg-[#0b1120] text-white' : 'bg-white text-slate-900'
        }`}>
          {/* Top subtle blue/purple line - internal accent */}
          <div className="absolute top-0 left-8 w-20 h-[3px] bg-gradient-to-r from-blue-500 to-purple-500 rounded-b-full z-10"></div>
          
        {/* Drawer Header */}
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-6 h-6 text-cyan-400" />
            <h3 className="font-black text-xl tracking-tight">Shopping Cart</h3>
            <span className="px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 text-xs font-bold border border-cyan-500/30">
              {cartItems.length} items
            </span>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-xl bg-slate-900/95 hover:bg-rose-500 text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Cart Items List */}
        <div className="p-6 flex-1 overflow-y-auto custom-scrollbar space-y-4">
          {cartItems.length === 0 ? (
            <div className="text-center py-16 space-y-4">
              <div className="w-16 h-16 rounded-full bg-slate-800/60 border border-slate-700 flex items-center justify-center mx-auto text-slate-400">
                <ShoppingBag className="w-8 h-8" />
              </div>
              <div>
                <p className="font-bold text-lg">Your Cart is Empty</p>
                <p className="text-xs text-slate-400 mt-1">Explore our 3D document templates and add items to your cart.</p>
              </div>
            </div>
          ) : (
            cartItems.map((item, idx) => (
              <div
                key={`${item.product.id}-${item.selectedFormat}-${idx}`}
                className="p-4 rounded-2xl bg-slate-900/60 border border-white/10 flex items-center gap-4 group relative"
              >
                <img
                  src={getSafeProductFirstImage(item.product)}
                  alt={item.product.title}
                  referrerPolicy="no-referrer"
                  className="w-16 h-16 rounded-xl object-cover border border-white/10 cursor-pointer"
                  onClick={() => {
                    if (onNavigateProduct) {
                      onNavigateProduct(item.product);
                      onClose();
                    }
                  }}
                />

                <div className="flex-1 min-w-0 cursor-pointer group" onClick={() => {
                  if (onNavigateProduct) {
                    onNavigateProduct(item.product);
                    onClose();
                  }
                }}>
                  <span className="text-[10px] font-bold text-cyan-400 uppercase">
                    .{item.selectedFormat}
                  </span>
                  <h4 className="font-extrabold text-sm truncate group-hover:text-cyan-400 transition-colors">
                    {item.product.title}
                  </h4>
                  <p className="text-xs font-black text-gradient mt-0.5">
                    {symbol}{(item.product.price * rate).toFixed(2)}
                  </p>
                </div>

                <div className="flex flex-col items-end gap-2 relative z-10">
                  <button
                    onClick={() => onRemoveItem(item.product.id)}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 transition-colors"
                    title="Remove"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <div className="flex items-center border border-white/20 rounded-lg overflow-hidden bg-slate-900">
                    <button
                      onClick={() => onUpdateQuantity(item.product.id, Math.max(1, item.quantity - 1))}
                      className="px-2 py-0.5 text-xs font-bold hover:bg-slate-800"
                    >
                      -
                    </button>
                    <span className="px-2 text-xs font-bold">{item.quantity}</span>
                    <button
                      onClick={() => onUpdateQuantity(item.product.id, item.quantity + 1)}
                      className="px-2 py-0.5 text-xs font-bold hover:bg-slate-800"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Drawer Footer & Checkout */}
        {cartItems.length > 0 && (
          <div className="p-6 pb-safe md:pb-[100px] border-t border-white/10 space-y-4 bg-slate-950/95 relative z-10 shrink-0">
            
            {/* Coupon Code Field */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={couponInput}
                  onChange={(e) => setCouponInput(e.target.value)}
                  placeholder="Promo Code (RH3D20)"
                  className="flex-1 px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-xs font-bold uppercase focus:outline-none focus:border-cyan-400"
                />
                <button
                  onClick={handleApply}
                  className="px-4 py-2 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white font-extrabold text-xs transition-colors"
                >
                  Apply
                </button>
              </div>
              {couponError && <p className="text-[11px] text-rose-400 font-bold">{couponError}</p>}
              {couponSuccess && <p className="text-[11px] text-emerald-400 font-bold">{couponSuccess}</p>}
              {appliedCoupon && (
                <div className="p-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-xs font-bold flex items-center justify-between">
                  <span>Coupon {appliedCoupon.code} (-{appliedCoupon.discountPercent}%)</span>
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                </div>
              )}
            </div>

            {/* Calculations Summary */}
            <div className="space-y-2 text-xs">
              <div className="flex justify-between text-slate-400">
                <span>Subtotal</span>
                <span className="font-bold text-white">{symbol}{(subtotal * rate).toFixed(2)}</span>
              </div>
              {appliedCoupon && (
                <div className="flex justify-between text-emerald-400 font-bold">
                  <span>Discount</span>
                  <span>-{symbol}{(discountAmount * rate).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm font-black pt-2 border-t border-white/10">
                <span>Total Amount</span>
                <span className="text-gradient text-base">{symbol}{(grandTotal * rate).toFixed(2)}</span>
              </div>
            </div>

            {/* Checkout Button */}
            <button
              onClick={() => {
                onProceedToCheckout();
                onClose();
              }}
              className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-blue-600 via-indigo-600 to-cyan-500 text-white font-extrabold text-sm flex items-center justify-center gap-2 shadow-xl shadow-blue-500/30 hover:shadow-cyan-500/50 hover:scale-[1.02] active:scale-98 transition-all duration-200"
            >
              <span>Proceed to Checkout</span>
              <ArrowRight className="w-4 h-4" />
            </button>

          </div>
        )}

        </div>
      </div>
    </div>
  );
};
